import React from 'react';
import { formatEnumValue } from 'formatters';
import { Country } from '@trnsact/trnsact-shared-types';
import { useCountries, useRegions } from 'modules/geo';
import { MUITable, MUITablePagination, MUITableSorting } from 'components/shared/MUITable';

import { DealSheetRulesTableRow } from '../types';
import { dealSheetConfigurationMapper } from '../../../mappers';
import { Actions, Description, RuleName, RuleValue } from './cells';

interface Props {
  isLoading: boolean;
  sorting: MUITableSorting;
  pagination: MUITablePagination;
  tableData: Record<string, any>[];
  actions: {
    onEdit: (id: string) => void;
    onViewDetails: (id: string) => void;
  };
}

export const DealSheetRulesTable = ({ tableData, actions, ...props }: Props) => {
  const { countries, loading: countriesLoading } = useCountries();
  const { regions: usRegions, loading: usRegionsLoading } = useRegions(Country.Us);
  const { regions: canadaRegions, loading: canadaRegionsLoading } = useRegions(Country.Ca);

  const mappedTableData: DealSheetRulesTableRow[] =
    !tableData.length || countriesLoading || usRegionsLoading || canadaRegionsLoading
      ? []
      : dealSheetConfigurationMapper.processDealSheetToTable(tableData, {
          countries,
          usRegions,
          canadaRegions,
        });

  return (
    <MUITable<DealSheetRulesTableRow>
      stickyHeader
      tableData={mappedTableData}
      {...props}
      columns={[
        {
          header: 'Rule',
          renderKey: 'name',
          sortKey: 'rule_name',
          renderCell: rule => <RuleName rule={rule} />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Description',
          sortKey: 'description',
          renderKey: 'description',
          renderCell: rule => <Description rule={rule} />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Customer',
          renderKey: 'propCustomerType',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propCustomerType" formatter={formatEnumValue} />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Payment',
          renderKey: 'propCustomerPaymentType',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propCustomerPaymentType" formatter={formatEnumValue} />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Country',
          renderKey: 'propLocationCountry',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationCountry" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'State',
          renderKey: 'propLocationState',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationState" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Province',
          renderKey: 'propLocationProvince',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationProvince" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'County',
          renderKey: 'propLocationCounty',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationCounty" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'City',
          renderKey: 'propLocationCity',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationCity" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'ZIP Code',
          renderKey: 'propLocationZip',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationZip" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Postal Code',
          renderKey: 'propLocationPostalCode',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propLocationPostalCode" />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Equipment',
          renderKey: 'propEquipmentType',
          renderCell: rule => <RuleValue rule={rule} ruleKey="propEquipmentType" formatter={formatEnumValue} />,
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
        },
        {
          header: 'Actions',
          renderKey: 'id',
          cellProps: { align: 'left' },
          headerCell: { align: 'left' },
          renderCell: rule => (
            <Actions
              rule={rule}
              actions={{
                onEdit: () => actions.onEdit(rule.id),
                onViewDetails: () => actions.onViewDetails(rule.id),
              }}
            />
          ),
        },
      ]}
    />
  );
};
